<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { useGlobalStore } from '@/stores/global'
import type { ComponentExposed } from 'vue-component-type-helpers'
import { computed, defineAsyncComponent, provide, ref, watch, watchEffect } from 'vue'
import AwaitableModal from './components/shared/AwaitableModal.vue'
import { useVideoCall } from './composables/useVideoCall'
import { useGroupCall } from './composables/useGroupCall'
import { useStudentCallFlow } from './composables/useStudentCallFlow'
import {
  normalizeRealtimeBusMessage,
  useRealtimeBus,
  type RealtimeBusNotification
} from './composables/useRealtimeBus'
import { useNotificationsRealtime } from './composables/useNotificationsRealtime'
import { sendStudentSessionEvent } from './services/students'
import { endSessionCall, leaveSessionCall } from './services/session-calls'
import { auth } from './stores/auth'
import { storeToRefs } from 'pinia'
import { UserRole } from './types/global'
import { formatPageTitle } from './router'
import { useTrackingSession } from './services/tracking-sessions'

const appStore = useGlobalStore()
const route = useRoute()
const VideoCallModal = import.meta.env.SSR
  ? null
  : defineAsyncComponent(() => import('./components/VideoCallModal.vue'))
const {
  _showVideoCallModal,
  _videoCallSession,
  _videoCallStudent,
  _videoCallId,
  _videoCallAgoraUID,
  _videoCallParticipants,
  _videoCallRoomType,
  closeVideoCall
} = useVideoCall()
const GroupCallModal = import.meta.env.SSR
  ? null
  : defineAsyncComponent(() => import('./components/GroupCallModal.vue'))
const { isGroupCallOpen, groupCallId, closeGroupCall } = useGroupCall()
const {
  state: studentCallState,
  markConnected,
  markLeaving,
  markFailed,
  markIdle
} = useStudentCallFlow()
const authStore = auth()
useTrackingSession()
const { user, isStudent } = storeToRefs(authStore)
const canUpdatePresence = computed(() => authStore.ability.can('update', 'Presence'))
const shouldSendPresence = computed(() => isStudent.value && canUpdatePresence.value)

type StudentCallEndedMessage = {
  event?: string
  session_call_id?: number | false | { id: number }
}

function getCallID(value: StudentCallEndedMessage['session_call_id']) {
  if (!value) return undefined
  return typeof value === 'number' ? value : value.id
}

function handleStudentCallEnded(notification: RealtimeBusNotification<StudentCallEndedMessage>) {
  const message = normalizeRealtimeBusMessage<StudentCallEndedMessage>(
    notification.message ?? notification
  )
  if (message.event !== 'call_ended') return

  const endedCallID = getCallID(message.session_call_id)
  if (endedCallID && _videoCallId.value && endedCallID !== _videoCallId.value) return

  closeVideoCall()
  markIdle()
}
const studentPresencePayload = JSON.stringify({
  event_name: 'update_presence',
  data: {
    inactivity_period: 60,
    im_status_ids_by_model: {}
  }
})

// Keep the private notification inbox connected across all authenticated
// routes. It is intentionally separate from page-level presence/call buses.
useNotificationsRealtime()

const awaitableModal = ref<null | ComponentExposed<typeof AwaitableModal>>(null)

watchEffect(() => {
  if (typeof document === 'undefined') return

  document.title = formatPageTitle(route.meta.title)
})

function sendStudentPresenceUpdate() {
  if (!shouldSendPresence.value || studentPresenceBus.status.value !== 'OPEN') return

  studentPresenceBus.send(studentPresencePayload)
}

const studentPresenceBus = useRealtimeBus({
  enabled: shouldSendPresence,
  onConnected: sendStudentPresenceUpdate
})

// The video call modal belongs to the app shell and can remain open while a
// student navigates away from the portal (for example, to past evaluations).
// Listen globally so a teacher ending the room always closes that modal.
useRealtimeBus<StudentCallEndedMessage>({
  channels: computed(() => ['call_ended']),
  enabled: isStudent,
  onNotification: handleStudentCallEnded
})

watch(
  () => route.fullPath,
  () => {
    sendStudentPresenceUpdate()
  },
  { immediate: true }
)

async function handleStudentCallJoined() {
  const sessionId = _videoCallSession.value?.id

  if (_videoCallId.value) return
  if (user.value?.maqari_role !== UserRole.STUDENT || !sessionId) return

  markConnected()

  try {
    await sendStudentSessionEvent(sessionId, 'call_started')
  } catch (error) {
    console.error('Failed to publish call_started event:', error)
  }
}

async function handleVideoCallEnded(studentId?: number) {
  const sessionId = _videoCallSession.value?.id
  const callID = _videoCallId.value

  if (callID) {
    try {
      await endSessionCall(callID)
      if (user.value?.maqari_role === UserRole.STUDENT) {
        markIdle()
      }
    } catch (error) {
      console.error('Failed to end session call:', error)
    }
    return
  }
  if (!sessionId) return

  if (user.value?.maqari_role === UserRole.STUDENT) {
    markLeaving()
  }

  try {
    await sendStudentSessionEvent(
      sessionId,
      user.value?.maqari_role === UserRole.STUDENT ? 'call_paused' : 'call_ended',
      user.value?.maqari_role === UserRole.STUDENT ? undefined : studentId
    )
  } catch (error) {
    console.error('Failed to publish call leave event:', error)
  } finally {
    if (user.value?.maqari_role === UserRole.STUDENT) {
      markIdle()
    }
  }
}

async function handleStudentCallLeft(studentId?: number) {
  const sessionId = _videoCallSession.value?.id
  const callID = _videoCallId.value

  if (callID) {
    try {
      await leaveSessionCall(callID)
      if (user.value?.maqari_role === UserRole.STUDENT) {
        markIdle()
      }
    } catch (error) {
      console.error('Failed to leave session call:', error)
    }
    return
  }
  if (!sessionId) return

  if (user.value?.maqari_role === UserRole.STUDENT) {
    markLeaving()
  }

  try {
    await sendStudentSessionEvent(sessionId, 'call_paused', studentId)
  } catch (error) {
    console.error('Failed to publish call leave event:', error)
  } finally {
    if (user.value?.maqari_role === UserRole.STUDENT) {
      markIdle()
    }
  }
}

function handleStudentCallFailed(message: string) {
  if (user.value?.maqari_role !== UserRole.STUDENT) return

  markFailed(message)
  closeVideoCall()
}

function handleVideoCallClose() {
  if (user.value?.maqari_role === UserRole.STUDENT && studentCallState.value === 'joining') {
    markFailed('تم إلغاء محاولة الانضمام إلى المكالمة.')
  }

  closeVideoCall()
}

provide('modal', awaitableModal)
</script>

<template>
  <template v-if="appStore.loading">
    <div class="fixed w-screen h-screen top-0 left-0 bg-black bg-opacity-50 z-[99]" />
    <div
      class="fixed bg-white dark:bg-gray-800 rounded-lg p-5 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[99] flex flex-col gap-3 items-center w-full max-w-xs"
    >
      <span class="icon-[eos-icons--loading] size-6 dark:text-gray-200"></span>
      <div class="dark:text-gray-200">جاري التحميل</div>
    </div>
  </template>

  <AwaitableModal ref="awaitableModal" />

  <RouterView :key="$route.fullPath" />

  <component
    :is="VideoCallModal"
    v-if="VideoCallModal"
    :show="_showVideoCallModal"
    :session="_videoCallSession"
    :student="_videoCallStudent"
    :call-id="_videoCallId"
    :agora-uid="_videoCallAgoraUID"
    :participants="_videoCallParticipants"
    :room-type="_videoCallRoomType"
    @close="handleVideoCallClose"
    @call-joined="handleStudentCallJoined"
    @call-left="handleStudentCallLeft"
    @call-ended="handleVideoCallEnded"
    @call-failed="handleStudentCallFailed"
  />

  <component
    :is="GroupCallModal"
    v-if="GroupCallModal"
    :show="isGroupCallOpen"
    :group-call-id="groupCallId"
    @close="closeGroupCall"
  />
</template>

<style>
.router-link-active {
  @apply bg-white dark:bg-gray-700 text-primary dark:text-blue-400 rounded-lg fill-primary dark:fill-blue-400;
}

.dp__theme_light {
  --dp-primary-color: var(--color-primary);
}

.dp__theme_dark {
  --dp-primary-color: var(--color-primary);
  --dp-background-color: #1f2937;
}
</style>
